import { trackEvent } from "./mixpanel";

/**
 * Upgrade nudges for the web app. Mirrors the Android journey
 * (trialJourney.ts / freeMinutesJourney.ts) with the same prompt ids, so the
 * platforms can be compared in Mixpanel through upgrade_prompt_shown and
 * upgrade_prompt_clicked.
 *
 * Every nudge is shown at most once per "episode": per trial for trial
 * nudges, per weekly allowance for free-minute nudges. The ledger lives in
 * localStorage, scoped to the signed-in account.
 */

export const UPGRADE_PROMPT_IDS = {
  trialWelcome: "trial_welcome",
  trialMidpoint: "trial_midpoint",
  trial3d: "trial_3d",
  trial1d: "trial_1d",
  trialEnded: "trial_ended",
  free50: "free_50pct",
  free20: "free_20pct",
  freeExhausted: "free_exhausted",
  // Upgrade tapped on the always-on minutes pill before 80% is used.
  freeMeter: "free_meter",
  freeReset: "free_reset",
  promptsLocked: "prompts_locked",
};

export const PRO_FEATURES = [
  "Unlimited dictation",
  "Unlimited audio transcription",
  "Create your own custom prompts",
  "Early access to new features",
];

const LEDGER_KEY = "zt_upgrade_ledger_v1";
const TRIAL_SEEN_KEY = "zt_trial_seen_v1";
const WEEK_SNAPSHOT_KEY = "zt_free_week_snapshot_v1";
const PROMPTS_DECLINE_KEY = "zt_prompts_lock_declines_v1";

const DAY_MS = 24 * 60 * 60 * 1000;
export const TRIAL_WELCOME_WINDOW_DAYS = 2;
export const TRIAL_ENDED_WINDOW_DAYS = 7;
const PROMPTS_QUIET_AFTER_DECLINES = 2;
const PROMPTS_QUIET_DAYS = 14;

/* ---------------------------- storage helpers ---------------------------- */

const readJson = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the nudge may show again, which is acceptable */
  }
};

const scoped = (scope, key) => `${scope || "anon"}::${key}`;

/** True when promptId was already shown in this episode. */
export const wasShown = (scope, promptId, episode) => {
  const ledger = readJson(LEDGER_KEY, {});
  return ledger[scoped(scope, promptId)] === String(episode);
};

const markShown = (scope, promptId, episode) => {
  const ledger = readJson(LEDGER_KEY, {});
  ledger[scoped(scope, promptId)] = String(episode);
  writeJson(LEDGER_KEY, ledger);
};

/** Retires a prompt for this episode without logging it as shown. */
export const retirePrompt = (scope, promptId, episode) => {
  if (episode != null) markShown(scope, promptId, episode);
};

/* ------------------------------- analytics ------------------------------- */

export const trackPromptShown = (promptId, surface) =>
  trackEvent("upgrade_prompt_shown", { prompt_id: promptId, surface, platform: "web" });

export const trackPromptClicked = (promptId, surface, action = "upgrade") =>
  trackEvent("upgrade_prompt_clicked", {
    prompt_id: promptId,
    surface,
    action,
    platform: "web",
  });

/**
 * Marks a prompt as shown for this episode and logs it, once. Returns false
 * when it had already been shown, so callers can skip rendering.
 */
export const claimPrompt = (scope, promptId, episode, surface) => {
  if (episode == null || wasShown(scope, promptId, episode)) return false;
  markShown(scope, promptId, episode);
  trackPromptShown(promptId, surface);
  return true;
};

/* -------------------------------- episodes -------------------------------- */

/** The weekly allowance is identified by the date it resets. */
export const weekEpisode = (usage) =>
  usage?.endDate ? usage.endDate.toISOString().slice(0, 10) : null;

/** A trial is identified by the date it ends. */
export const trialEpisode = (usage) =>
  usage?.trialEndsOn ? usage.trialEndsOn.toISOString().slice(0, 10) : null;

/* ---------------------------------- copy ---------------------------------- */

export const fmtDay = (date) =>
  date
    ? date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";

/**
 * The trial banner stage for the days left, or null. Matches Android:
 * halfway only for trials of 10 days or more, then 3 days and 1 day left.
 */
export const trialBannerStage = (usage) => {
  if (!usage?.isTrial || usage.trialDaysLeft == null) return null;
  const left = usage.trialDaysLeft;
  if (left <= 0) return null;
  if (left <= 1) return UPGRADE_PROMPT_IDS.trial1d;
  if (left <= 3) return UPGRADE_PROMPT_IDS.trial3d;
  const length = usage.trialLengthDays || 7;
  if (length >= 10 && left <= Math.ceil(length / 2)) return UPGRADE_PROMPT_IDS.trialMidpoint;
  return null;
};

export const trialBannerCopy = (stage, usage) => {
  const end = fmtDay(usage?.trialEndsOn);
  switch (stage) {
    case UPGRADE_PROMPT_IDS.trialMidpoint:
      return {
        title: `${usage.trialDaysLeft} days left in your Pro trial`,
        body: "Keep Pro to stay unlimited after your trial.",
        cta: "Keep Pro",
      };
    case UPGRADE_PROMPT_IDS.trial3d:
      return {
        title: `Your Pro trial ends in ${usage.trialDaysLeft} days`,
        body: end
          ? `Upgrade to keep unlimited dictation and custom prompts after ${end}.`
          : "Upgrade to keep unlimited dictation and custom prompts.",
        cta: "Keep Pro",
      };
    case UPGRADE_PROMPT_IDS.trial1d:
      return {
        title: "Your Pro trial ends tomorrow",
        body: "Keep unlimited dictation and custom prompts.",
        cta: "Keep Pro",
      };
    default:
      return null;
  }
};

/* ------------------------------ trial memory ------------------------------ */

/** Remembers that this account was on a trial, so its end can be noticed. */
export const rememberTrial = (scope, usage) => {
  if (!usage?.isTrial || !usage.trialEndsOn) return;
  const seen = readJson(TRIAL_SEEN_KEY, {});
  seen[scope || "anon"] = usage.trialEndsOn.toISOString();
  writeJson(TRIAL_SEEN_KEY, seen);
};

/**
 * The end date of a trial this browser saw, if it ended within the last
 * week and the account is now on Free. Otherwise null.
 */
export const recentlyEndedTrial = (scope, usage) => {
  if (!usage || usage.isPro || usage.isTrial) return null;
  const seen = readJson(TRIAL_SEEN_KEY, {});
  const iso = seen[scope || "anon"];
  if (!iso) return null;
  const ended = new Date(iso);
  if (Number.isNaN(ended.getTime())) return null;
  const age = Date.now() - ended.getTime();
  if (age < 0 || age > TRIAL_ENDED_WINDOW_DAYS * DAY_MS) return null;
  return ended;
};

/* --------------------------- weekly free minutes --------------------------- */

/**
 * Compares this week's usage with the snapshot from the last visit and
 * returns last week's use when the allowance has just refilled for someone
 * who used at least half of it. Always updates the snapshot.
 */
export const checkMinutesBack = (scope, usage) => {
  if (!usage || usage.isPro || usage.totalSec == null) return null;
  const episode = weekEpisode(usage);
  if (!episode) return null;
  const all = readJson(WEEK_SNAPSHOT_KEY, {});
  const key = scope || "anon";
  const prev = all[key];
  let result = null;
  if (
    prev &&
    prev.episode !== episode &&
    prev.episode < episode &&
    prev.totalSec > 0 &&
    prev.usedSec / prev.totalSec >= 0.5
  ) {
    result = { lastWeekUsedMin: Math.round(prev.usedSec / 60) };
  }
  const usedSec =
    prev && prev.episode === episode ? Math.max(prev.usedSec, usage.usedSec) : usage.usedSec;
  all[key] = { episode, usedSec, totalSec: usage.totalSec };
  writeJson(WEEK_SNAPSHOT_KEY, all);
  return result;
};

/** Fraction of the weekly allowance still left, or null when not Free. */
export const freeFractionLeft = (usage) => {
  if (!usage || usage.isPro || !usage.totalSec) return null;
  return Math.max(0, usage.remainingSec) / usage.totalSec;
};

/* -------------------------- custom prompts declines -------------------------- */

/** True while "Not now" has been tapped twice in the last 14 days. */
export const promptsLockQuiet = () => {
  const d = readJson(PROMPTS_DECLINE_KEY, null);
  return Boolean(d?.quietUntil && Date.now() < d.quietUntil);
};

export const recordPromptsLockDecline = () => {
  const d = readJson(PROMPTS_DECLINE_KEY, { count: 0, quietUntil: 0 });
  const count = (d.quietUntil && Date.now() >= d.quietUntil ? 0 : d.count) + 1;
  const quietUntil =
    count >= PROMPTS_QUIET_AFTER_DECLINES ? Date.now() + PROMPTS_QUIET_DAYS * DAY_MS : 0;
  writeJson(PROMPTS_DECLINE_KEY, { count: quietUntil ? 0 : count, quietUntil });
};
